/**
 * "하루당" 옵션 — 상품별 데이터 플래그로 켜는 옵션 과금 규칙 (09-30, Bestbike 라이더 지원).
 *
 * `products.specifications.perDayOptionOrders: number[]` 에 선택형 옵션의
 * **display_order** 를 넣으면 그 옵션은 "선택지 단가 × 대여일수" 로 과금된다.
 * 옵션 id 가 아니라 display_order 인 이유는 편도 반납(`oneWayReturnOptionOrder`)과 같다 —
 * 옵션 행은 언어별로 id 가 다르고 마법사 저장마다 새로 발급된다.
 *
 * 켜진 옵션만:
 *  - 수량 = 대여일수(고객이 고르지 않는다, 수량 입력 없음). 클라이언트가 보낸 수량은
 *    믿지 않고 서버가 플래그 + 대여 기간으로 다시 정한다(`resolvePerDayOptionQuantity`).
 *  - 대여일수 = 기본 요금에 쓰는 것과 같은 값(`calculateRentalDays`, 달력일 포함 계산).
 *    타임슬롯 = 1, 옵션 단독 구매(대여 없음) = 0 → 고객 수량 그대로(기간이 없으므로).
 *
 * 지정이 없는 상품(다른 파트너 전부)은 아무 변화가 없다.
 */

export const PER_DAY_OPTION_SPEC_KEY = "perDayOptionOrders";

const EMPTY: ReadonlySet<number> = new Set<number>();

/** `specifications` 에서 하루당 옵션의 display_order 목록을 읽는다(없거나 형식이 틀리면 빈 집합). */
export function readPerDayOptionOrders(
  specifications: unknown,
): ReadonlySet<number> {
  if (!specifications || typeof specifications !== "object") return EMPTY;
  const raw = (specifications as Record<string, unknown>)[PER_DAY_OPTION_SPEC_KEY];
  if (!Array.isArray(raw) || raw.length === 0) return EMPTY;
  const orders = raw.filter(
    (v): v is number => typeof v === "number" && Number.isInteger(v) && v >= 0,
  );
  return orders.length === 0 ? EMPTY : new Set(orders);
}

/** 이 display_order 의 옵션이 하루당 옵션인지. */
export function isPerDayOptionOrder(
  orders: ReadonlySet<number>,
  displayOrder: number | null | undefined,
): boolean {
  return displayOrder != null && orders.has(displayOrder);
}

/**
 * 옵션 계산에 쓰는 대여일수 — 서버 `validateDirectCheckoutPrice`·`getCart` 와 같은 규칙.
 * daily = 달력일 포함 일수(없으면 1), timeslot = 1, option_only = 0.
 */
export function perDayRentalDays(
  rentalType: "daily" | "timeslot" | "option_only" | string,
  dailyDays: number | null | undefined,
): number {
  if (rentalType === "option_only") return 0;
  if (rentalType === "timeslot") return 1;
  return dailyDays != null && dailyDays >= 1 ? dailyDays : 1;
}

/**
 * 옵션 한 줄의 과금 수량. 하루당 옵션이고 대여 기간이 있으면 대여일수로 **강제**,
 * 아니면 요청 수량 그대로(기존 동작).
 */
export function resolvePerDayOptionQuantity({
  isPerDay,
  rentalDays,
  requestedQuantity,
}: {
  isPerDay: boolean;
  rentalDays: number;
  requestedQuantity: number;
}): number {
  if (isPerDay && rentalDays >= 1) return rentalDays;
  return requestedQuantity;
}

/** 하루당 옵션 총액 = 단가 × 일수 (일수가 없으면 0). */
export function perDayOptionTotal(unitPrice: number, days: number | null | undefined): number {
  if (days == null || days < 1) return 0;
  return (unitPrice || 0) * days;
}

/**
 * 서버 재계산 결과(옵션 id → 강제 수량)를 저장용 옵션 배열에 덮어쓴다.
 * 결제 준비 경로(PortOne prepare-checkout · Stripe create-checkout-session)가
 * 클라이언트가 보낸 reservation_data 를 저장하기 전에 쓴다 — 웹훅은 저장된 수량으로
 * 재가격·reservation_items 를 쓰므로 여기서 대여일수로 맞춰 둬야 한다.
 */
export function applyPerDayQuantities<T extends { id?: string | null; quantity?: number }>(
  options: T[] | null | undefined,
  quantityByOptionId: ReadonlyMap<string, number>,
): T[] {
  if (!options) return [];
  if (quantityByOptionId.size === 0) return options;
  return options.map((opt) => {
    const forced = opt.id ? quantityByOptionId.get(opt.id) : undefined;
    return forced == null ? opt : { ...opt, quantity: forced };
  });
}

/** 서버 옵션 상세(`ValidatedPrice.optionDetails`)에서 하루당 옵션의 강제 수량만 뽑는다. */
export function perDayQuantityMap(
  details: ReadonlyArray<{ id: string; quantity: number; perDay: boolean }> | null | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const d of details ?? []) {
    if (d.perDay && d.quantity >= 1) map.set(d.id, d.quantity);
  }
  return map;
}

/**
 * 예약 내역 표시용 — 하루당 옵션의 option_id 집합을 만든다.
 * `optionRows` = product_options(id, product_id, display_order) 조회 결과,
 * `specificationsByProduct` = 상품별 specifications.
 */
export function perDayOptionIdSet(
  optionRows: Array<{ id: string; product_id: string; display_order: number | null }>,
  specificationsByProduct: ReadonlyMap<string, unknown>,
): Set<string> {
  const ids = new Set<string>();
  for (const row of optionRows) {
    const orders = readPerDayOptionOrders(specificationsByProduct.get(row.product_id));
    if (isPerDayOptionOrder(orders, row.display_order)) ids.add(row.id);
  }
  return ids;
}
